<template>
  <div class="orders-new">
    <v-skeleton-loader
      v-show="isLoading"
      type="table"
    />

    <div v-show="!isLoading">
      <!-- ヘッダー: フィルター・設定 -->
      <v-card class="mb-4">
        <v-card-text class="d-flex align-center flex-wrap gap-2">
          <!-- ステータスフィルター -->
          <v-btn-toggle
            v-model="statusFilter"
            mandatory
            dense
            class="mr-4"
          >
            <v-btn
              small
              :value="'all'"
              :color="statusFilter === 'all' ? 'primary' : ''"
            >
              全て
            </v-btn>
            <v-btn
              small
              :value="'pending'"
              :color="statusFilter === 'pending' ? '#d4bc83' : ''"
              :class="{ 'pending-btn--active': statusFilter === 'pending' }"
            >
              未提供
              <v-chip
                v-if="pendingCount > 0"
                x-small
                class="pending-count-chip ml-1"
              >
                {{ pendingCount }}
              </v-chip>
            </v-btn>
            <v-btn
              small
              :value="'delivered'"
              :color="statusFilter === 'delivered' ? 'success' : ''"
            >
              提供済
            </v-btn>
          </v-btn-toggle>

          <!-- 表示モード切替 -->
          <v-btn-toggle
            v-model="viewMode"
            mandatory
            dense
            class="mr-4"
          >
            <v-btn
              small
              :value="'list'"
            >
              <v-icon small>
                {{ icons.mdiFormatListBulleted }}
              </v-icon>
            </v-btn>
            <v-btn
              small
              :value="'table'"
            >
              <v-icon small>
                {{ icons.mdiTableLarge }}
              </v-icon>
            </v-btn>
          </v-btn-toggle>

          <v-spacer />

          <!-- 印刷ボタン（分割: メイン=印刷実行 / ドロップダウン=プリンター設定） -->
          <div class="d-inline-flex print-split-btn">
            <v-btn
              small
              color="primary"
              class="print-split-btn__main"
              :disabled="!hasEnabledPrinters || selectedOrderKeys.size === 0"
              :loading="isPrintingSelected"
              @click="printSelectedOrders"
            >
              <v-icon
                small
                left
              >
                {{ icons.mdiPrinter }}
              </v-icon>
              印刷{{ selectedOrderKeys.size > 0 ? `(${selectedOrderKeys.size})` : '' }}
            </v-btn>
            <v-menu
              offset-y
              :close-on-content-click="false"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  small
                  color="primary"
                  class="print-split-btn__dropdown px-1"
                  min-width="0"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>
                    {{ icons.mdiMenuDown }}
                  </v-icon>
                </v-btn>
              </template>

              <v-card min-width="250">
                <v-card-subtitle class="pb-0 d-flex align-center justify-space-between">
                  <span>使用プリンター</span>
                  <v-btn
                    x-small
                    text
                    :loading="isAutoDetecting"
                    @click="onRetestAll"
                  >
                    再テスト
                  </v-btn>
                </v-card-subtitle>

                <v-list dense>
                  <v-list-item
                    v-for="printer in kitchenPrinters"
                    :key="printer.id"
                    @click="onTogglePrinter(printer.id)"
                  >
                    <v-list-item-action class="mr-2">
                      <v-checkbox
                        :input-value="isPrinterEnabled(printer.id)"
                        readonly
                        dense
                        hide-details
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ printer.attributes.name }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-icon class="ml-0">
                      <v-progress-circular
                        v-if="connectionStatuses[printer.id] === 'checking'"
                        indeterminate
                        size="16"
                        width="2"
                      />
                      <v-icon
                        v-else-if="connectionStatuses[printer.id] === 'connected'"
                        small
                        color="success"
                      >
                        {{ icons.mdiCheckCircle }}
                      </v-icon>
                      <v-icon
                        v-else-if="connectionStatuses[printer.id] === 'failed'"
                        small
                        color="error"
                      >
                        {{ icons.mdiCloseCircle }}
                      </v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>

          <!-- 通知音設定 -->
          <v-btn
            icon
            small
            :color="isSoundEnabled ? 'primary' : 'grey'"
            @click="toggleSound"
          >
            <v-icon>{{ isSoundEnabled ? icons.mdiVolumeHigh : icons.mdiVolumeOff }}</v-icon>
          </v-btn>

          <!-- キッチンディスプレイモード -->
          <v-btn
            small
            outlined
            @click="enterKitchenMode"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiFullscreen }}
            </v-icon>
            キッチン
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- リスト表示モード -->
      <div
        v-if="viewMode === 'list'"
        class="orders-list"
      >
        <v-card
          v-for="order in filteredOrders"
          :key="order.key"
          :class="[
            'order-card mb-2',
            { 'order-delivered': order.isDelivered },
            { 'order-new': order.isNew }
          ]"
          @click="toggleDelivered(order)"
        >
          <v-card-text class="d-flex align-center py-2">
            <!-- 印刷選択チェックボックス -->
            <v-checkbox
              v-if="hasEnabledPrinters"
              :input-value="selectedOrderKeys.has(order.key)"
              dense
              hide-details
              class="mt-0 mr-2 shrink"
              @click.stop="toggleOrderSelection(order.key)"
            />

            <!-- ステータスアイコン -->
            <v-icon
              :color="order.isDelivered ? 'success' : 'warning'"
              class="mr-3"
            >
              {{ order.isDelivered ? icons.mdiCheckCircle : icons.mdiClockOutline }}
            </v-icon>

            <!-- 注文内容 -->
            <div class="flex-grow-1">
              <div class="d-flex align-center mb-1">
                <span class="font-weight-bold mr-2">{{ order.tableName }}</span>
                <span class="text-caption text--secondary">{{ order.time }}</span>
                <v-chip
                  v-if="order.isSelfOrder"
                  x-small
                  color="info"
                  class="ml-2"
                >
                  セルフ
                </v-chip>
              </div>
              <div class="d-flex align-center">
                <span class="text-body-1">{{ order.vendibleName }}</span>
                <span
                  v-if="order.quantity > 1"
                  class="ml-2 font-weight-bold"
                >
                  x{{ order.quantity }}
                </span>
              </div>
              <div
                v-if="order.selectedOptions && order.selectedOptions.length > 0"
                class="text-caption text--secondary"
              >
                <v-chip
                  v-for="(opt, idx) in order.selectedOptions"
                  :key="idx"
                  x-small
                  outlined
                  class="mr-1"
                >
                  {{ opt.option_name }}
                </v-chip>
              </div>
              <div
                v-if="order.recipientName"
                class="text-caption text--secondary"
              >
                {{ order.recipientName }}
              </div>
              <!-- 商品タグ -->
              <div
                v-if="order.tagNames.length > 0"
                class="mt-1"
              >
                <v-chip
                  v-for="tagName in order.tagNames"
                  :key="`${order.key}-${tagName}`"
                  x-small
                  class="mr-1"
                >
                  {{ tagName }}
                </v-chip>
              </div>
            </div>

            <!-- 提供済みボタン -->
            <v-btn
              icon
              :color="order.isDelivered ? 'grey' : 'success'"
              :loading="order.isUpdating"
              @click.stop="toggleDelivered(order)"
            >
              <v-icon>{{ order.isDelivered ? icons.mdiUndo : icons.mdiCheck }}</v-icon>
            </v-btn>
          </v-card-text>
        </v-card>

        <div
          v-if="filteredOrders.length === 0"
          class="text-center py-8 text--secondary"
        >
          注文がありません
        </div>
      </div>

      <!-- 卓グループ表示モード -->
      <div
        v-else
        class="orders-by-table"
      >
        <v-expansion-panels
          v-model="expandedTables"
          multiple
        >
          <v-expansion-panel
            v-for="tableGroup in ordersByTable"
            :key="tableGroup.tableId"
          >
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <span class="font-weight-bold">{{ tableGroup.tableName }}</span>
                <v-badge
                  v-if="tableGroup.pendingCount > 0"
                  :content="tableGroup.pendingCount"
                  color="warning"
                  inline
                  class="ml-2"
                />
                <v-spacer />
                <v-btn
                  v-if="tableGroup.pendingCount > 0"
                  x-small
                  color="success"
                  class="mr-2"
                  @click.stop="markAllDelivered(tableGroup)"
                >
                  全て提供済み
                </v-btn>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list dense>
                <v-list-item
                  v-for="order in tableGroup.orders"
                  :key="order.key"
                  :class="{ 'order-delivered': order.isDelivered }"
                  @click="toggleDelivered(order)"
                >
                  <v-list-item-icon>
                    <v-icon
                      :color="order.isDelivered ? 'success' : 'warning'"
                      small
                    >
                      {{ order.isDelivered ? icons.mdiCheckCircle : icons.mdiClockOutline }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ order.vendibleName }}
                      <span v-if="order.quantity > 1">x{{ order.quantity }}</span>
                      <span
                        v-if="order.selectedOptions && order.selectedOptions.length > 0"
                        class="text-caption text--secondary ml-1"
                      >({{ order.selectedOptions.map(o => o.option_name).join(', ') }})</span>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ order.time }}
                      <span v-if="order.recipientName">- {{ order.recipientName }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>

    <!-- キッチンディスプレイモード -->
    <v-dialog
      v-model="isKitchenMode"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="kitchen-display">
        <v-toolbar
          dark
          color="primary"
        >
          <v-btn
            icon
            dark
            @click="isKitchenMode = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
          <v-toolbar-title>キッチンディスプレイ</v-toolbar-title>
          <v-spacer />
          <span class="text-h6">未提供: {{ pendingCount }}件</span>
        </v-toolbar>

        <v-container
          fluid
          class="kitchen-content"
        >
          <v-row>
            <v-col
              v-for="order in pendingOrders"
              :key="order.key"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <v-card
                class="kitchen-order-card"
                :class="{ 'order-new': order.isNew }"
                @click="toggleDelivered(order)"
              >
                <v-card-title class="text-h5">
                  {{ order.tableName }}
                </v-card-title>
                <v-card-text>
                  <div class="text-h4 mb-2">
                    {{ order.vendibleName }}
                    <span v-if="order.quantity > 1">x{{ order.quantity }}</span>
                  </div>
                  <div
                    v-if="order.selectedOptions && order.selectedOptions.length > 0"
                    class="text-h6 mb-1"
                  >
                    {{ order.selectedOptions.map(o => o.option_name).join(' / ') }}
                  </div>
                  <div class="text-h6 text--secondary">
                    {{ order.time }}
                  </div>
                  <div
                    v-if="order.recipientName"
                    class="text-subtitle-1"
                  >
                    {{ order.recipientName }}
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    block
                    x-large
                    outlined
                    color="success"
                    :loading="order.isUpdating"
                    @click.stop="toggleDelivered(order)"
                  >
                    <v-icon left>
                      {{ icons.mdiCheckCircleOutline }}
                    </v-icon>
                    提供完了にする
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>

          <div
            v-if="pendingOrders.length === 0"
            class="text-center py-16"
          >
            <v-icon
              size="100"
              color="grey"
            >
              {{ icons.mdiTrayFull }}
            </v-icon>
            <div class="text-h4 mt-4 white--text">
              待機中...
            </div>
            <div class="text-subtitle-1 mt-2 grey--text">
              新しい注文が入ると表示されます
            </div>
            <div
              v-if="orders.length > 0"
              class="mt-4"
            >
              <v-chip
                color="success"
                outlined
              >
                本日 {{ orders.length }}件 提供済み
              </v-chip>
            </div>
          </div>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref, computed, getCurrentInstance, onMounted, watch,
} from '@vue/composition-api'
import {
  mdiCheckCircle,
  mdiClockOutline,
  mdiCheck,
  mdiUndo,
  mdiFormatListBulleted,
  mdiTableLarge,
  mdiVolumeHigh,
  mdiVolumeOff,
  mdiFullscreen,
  mdiClose,
  mdiCloseCircle,
  mdiCheckCircleOutline,
  mdiTrayFull,
  mdiPrinter,
  mdiMenuDown,
} from '@mdi/js'
import {
  map,
  chain,
  groupBy,
  flatMap,
  findIndex,
  each,
} from 'lodash'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import useDateFormat from '@/views/composable/useDateFormat'
import useKitchenPrint from '@/views/composable/useKitchenPrint'
import useKitchenPrinterSelection from '@/views/composable/useKitchenPrinterSelection'

export default {
  name: 'OrdersNew',
  setup() {
    const vm = getCurrentInstance().proxy

    const { hhmm } = useDateFormat()
    const { kitchenPrinters, fetchKitchenPrinters, printKitchenSlips } = useKitchenPrint()
    const {
      isPrinterEnabled, togglePrinter, connectionStatuses,
      isAutoDetecting, autoDetectPrinters, isFirstTime,
      hasAnyEnabled,
    } = useKitchenPrinterSelection()

    const initKitchenPrinters = async () => {
      await fetchKitchenPrinters()
      if (isFirstTime() && kitchenPrinters.value.length > 0) {
        autoDetectPrinters(kitchenPrinters.value)
      }
    }
    initKitchenPrinters()

    const onTogglePrinter = printerId => {
      const allPrinterIds = kitchenPrinters.value.map(p => p.id)
      togglePrinter(printerId, allPrinterIds)
    }

    const onRetestAll = () => {
      autoDetectPrinters(kitchenPrinters.value)
    }

    const isLoading = ref(false)
    const printingOrderKeys = ref(new Set())
    const vendingHistories = ref([])
    const statusFilter = ref('pending')
    const viewMode = ref('list')
    const expandedTables = ref([])
    const isKitchenMode = ref(false)
    const newOrderIds = ref(new Set())
    const updatingOrderKeys = ref(new Set())
    const selectedOrderKeys = ref(new Set())
    const isPrintingSelected = ref(false)

    // 有効なプリンターが1台以上あるか
    const hasEnabledPrinters = computed(() => hasAnyEnabled(kitchenPrinters.value))

    // 通知音設定
    const isSoundEnabled = computed(() => vm.$store?.state?.selfOrder?.soundEnabled ?? true)

    const toggleSound = () => {
      vm.$store.dispatch('selfOrder/setSoundEnabled', !isSoundEnabled.value)
    }

    // 注文データを表示用に整形（古い注文が先頭）
    const orders = computed(() => {
      return chain(vendingHistories.value)
        .orderBy('attributes.createdAt', ['asc'])
        .groupBy(vendingHistory => {
          const {
            vendibleName, isDelivered, createdAt,
          } = vendingHistory.attributes
          const { tableId } = vendingHistory.meta
          const tagNames = map(vendingHistory.attributes.vendibleTags?.data || [], 'attributes.name').join('')
          const time = hhmm(createdAt)
          const recipientIds = map(vendingHistory.attributes.vendibleRecipients?.data || [], 'attributes.userId').join(',')
          const selectedOptionsKey = (vendingHistory.attributes.selectedOptions || []).map(o => `${o.group_id}:${o.option_id}`).join(',')

          return `${vendibleName}${tableId}${recipientIds}${tagNames}${time}${isDelivered}${selectedOptionsKey}`
        })
        .mapValues((vendingHistoriesGrouped, key) => {
          const target = vendingHistoriesGrouped[0]
          const {
            vendibleName, isDelivered, createdAt,
          } = target.attributes
          const { tableId, tableName } = target.meta
          const tagNames = map(target.attributes.vendibleTags?.data || [], 'attributes.name')
          const time = hhmm(createdAt)
          const recipientName = map(target.attributes.vendibleRecipients?.data || [], 'attributes.recipient.data.attributes.name').join(', ') || null
          const quantity = vendingHistoriesGrouped.length
          // セルフオーダー判定（vendible_recipientsが空の場合はセルフオーダーの可能性）
          const isSelfOrder = vendingHistoriesGrouped.some(vh => vh.attributes.orderSource === 'self_order')

          const selectedOptions = target.attributes.selectedOptions || []

          return {
            key,
            id: target.id,
            vendibleName,
            isDelivered,
            time,
            tagNames,
            tableId,
            tableName,
            recipientName,
            quantity,
            isSelfOrder,
            selectedOptions,
            isNew: newOrderIds.value.has(target.id),
            isUpdating: updatingOrderKeys.value.has(key),
            actualData: vendingHistoriesGrouped,
          }
        })
        .values()
        .value()
    })

    // フィルター済み注文
    const filteredOrders = computed(() => {
      if (statusFilter.value === 'all') return orders.value
      if (statusFilter.value === 'pending') return orders.value.filter(o => !o.isDelivered)

      return orders.value.filter(o => o.isDelivered)
    })

    // 未提供注文
    const pendingOrders = computed(() => orders.value.filter(o => !o.isDelivered))
    const pendingCount = computed(() => pendingOrders.value.length)

    // 卓ごとにグループ化
    const ordersByTable = computed(() => {
      const grouped = groupBy(filteredOrders.value, 'tableId')

      return Object.entries(grouped).map(([tableId, tableOrders]) => ({
        tableId,
        tableName: tableOrders[0]?.tableName || `卓${tableId}`,
        orders: tableOrders,
        pendingCount: tableOrders.filter(o => !o.isDelivered).length,
      }))
    })

    // データ取得
    const getVendingHistories = async () => {
      isLoading.value = true

      const res = await VendingHistoryApi.getVendingHistories({
        checkedOut: false,
        beforeCheckOut: true,
        isToday: true,
        vendibleType: 'Item',
      })

      if (res?.data?.data) {
        vendingHistories.value = res.data.data
      }

      isLoading.value = false
    }

    // 提供済みステータスの切り替え
    const toggleDelivered = async order => {
      updatingOrderKeys.value.add(order.key)

      const ids = flatMap(order.actualData, 'id')
      const newIsDelivered = !order.isDelivered

      try {
        const res = await VendingHistoryApi.updateAllVendingHistories({
          ids,
          attributes: {
            isDelivered: newIsDelivered,
          },
        })

        if (res.data) {
          each(res.data.vendingHistories.data, data => {
            const vendingHistoryIdx = findIndex(vendingHistories.value, o => Number(o.id) === Number(data.id))
            if (vendingHistoryIdx === -1) return

            vendingHistories.value.splice(vendingHistoryIdx, 1, data)
          })

          vm.$toast.success(newIsDelivered ? '提供済みにしました' : '未提供に戻しました')
        }
      } catch (error) {
        console.error('Failed to update order status:', error)
        vm.$toast.error('更新に失敗しました')
      } finally {
        updatingOrderKeys.value.delete(order.key)
      }
    }

    // 卓内の全注文を提供済みに
    const markAllDelivered = async tableGroup => {
      const pendingOrdersList = tableGroup.orders.filter(o => !o.isDelivered)
      const ids = flatMap(pendingOrdersList, o => flatMap(o.actualData, 'id'))

      try {
        const res = await VendingHistoryApi.updateAllVendingHistories({
          ids,
          attributes: {
            isDelivered: true,
          },
        })

        if (res.data) {
          each(res.data.vendingHistories.data, data => {
            const vendingHistoryIdx = findIndex(vendingHistories.value, o => Number(o.id) === Number(data.id))
            if (vendingHistoryIdx === -1) return

            vendingHistories.value.splice(vendingHistoryIdx, 1, data)
          })

          vm.$toast.success(`${tableGroup.tableName}の注文を全て提供済みにしました`)
        }
      } catch (error) {
        console.error('Failed to update orders:', error)
        vm.$toast.error('更新に失敗しました')
      }
    }

    const enterKitchenMode = () => {
      isKitchenMode.value = true
    }

    // 注文選択のトグル
    const toggleOrderSelection = key => {
      const next = new Set(selectedOrderKeys.value)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      selectedOrderKeys.value = next
    }

    // 選択した注文をキッチン印刷
    const printSelectedOrders = () => {
      if (selectedOrderKeys.value.size === 0) return

      isPrintingSelected.value = true
      const selectedOrders = orders.value.filter(o => selectedOrderKeys.value.has(o.key))

      selectedOrders
        .reduce((promise, order) => promise.then(() => {
          printingOrderKeys.value.add(order.key)

          return printKitchenSlips({
            vendingHistories: order.actualData,
            staffName: '',
            hallName: '',
            tableCode: order.tableName,
            customerCount: 0,
          }).then(() => {
            printingOrderKeys.value.delete(order.key)
          })
        }), Promise.resolve())
        .then(() => {
          selectedOrderKeys.value = new Set()
          isPrintingSelected.value = false
        })
    }

    // 初期データ取得
    getVendingHistories()

    // オーダー画面を開いたときに未読カウントをクリア
    onMounted(() => {
      vm.$store.dispatch('selfOrder/clearUnreadCount')
    })

    // 新規注文があったらデータを再取得
    const latestOrder = computed(() => vm.$store.state.selfOrder?.latestOrder)
    watch(latestOrder, newOrder => {
      if (newOrder) {
        // 新規注文のハイライト用にIDを記録
        if (newOrder.items) {
          newOrder.items.forEach(item => newOrderIds.value.add(item.id))
          // 5秒後にハイライトを解除
          setTimeout(() => {
            newOrder.items.forEach(item => newOrderIds.value.delete(item.id))
          }, 5000)
        }
        getVendingHistories()
      }
    })

    return {
      // data
      isLoading,
      statusFilter,
      viewMode,
      expandedTables,
      isKitchenMode,

      kitchenPrinters,
      isPrinterEnabled,
      connectionStatuses,
      isAutoDetecting,
      onTogglePrinter,
      onRetestAll,

      // computed
      orders,
      filteredOrders,
      pendingOrders,
      pendingCount,
      ordersByTable,
      isSoundEnabled,

      printingOrderKeys,
      selectedOrderKeys,
      isPrintingSelected,
      hasEnabledPrinters,

      // methods
      getVendingHistories,
      toggleDelivered,
      markAllDelivered,
      toggleSound,
      enterKitchenMode,
      toggleOrderSelection,
      printSelectedOrders,

      icons: {
        mdiCheckCircle,
        mdiClockOutline,
        mdiCheck,
        mdiUndo,
        mdiFormatListBulleted,
        mdiTableLarge,
        mdiVolumeHigh,
        mdiVolumeOff,
        mdiFullscreen,
        mdiClose,
        mdiCloseCircle,
        mdiCheckCircleOutline,
        mdiTrayFull,
        mdiPrinter,
        mdiMenuDown,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
$gold: #d4bc83;
$gold-text: #3a2e1a;

.pending-btn--active {
  color: $gold-text !important;
}

::v-deep .pending-count-chip.v-chip {
  background-color: $gold !important;
  color: $gold-text !important;
  font-weight: bold;
  height: 18px;
  min-width: 18px;
  padding: 0 4px;
}

.print-split-btn {
  &__main {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  &__dropdown {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-left: 1px solid rgba(255, 255, 255, 0.3) !important;
  }
}

.orders-new {
  .order-card {
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &.order-delivered {
      opacity: 0.6;
      background-color: rgba(0, 0, 0, 0.02);
    }

    &.order-new {
      animation: pulse 1s ease-in-out 3;
      border-left: 4px solid #ff9800;
    }
  }

  .order-delivered {
    opacity: 0.6;
  }

  .gap-2 {
    gap: 8px;
  }
}

.kitchen-display {
  background-color: #1e1e1e;

  .kitchen-content {
    padding: 24px;
    min-height: calc(100vh - 64px);
  }

  .kitchen-order-card {
    height: 100%;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      transform: scale(1.02);
    }

    &.order-new {
      animation: pulse 1s ease-in-out infinite;
      border: 3px solid #ff9800;
    }
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 152, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
  }
}
</style>
